import { DEFAULT_LOOK_FIELDS, lookDataToRequestBody } from "../../../models/looker/look";
import { PromiseHandler } from "../../../handlers";
import { parseInclusionFilter } from "../../../handlers/filter";
import axios from "axios";
class LookHandler extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-looks");
        this.baseUrl = `${this.sdk.urls.burnsBaseUrl}/looker/looks`;
    }
    async findItems(filters) {
        const queryParams = this.filterToQueryParams(filters);
        const url = `${this.baseUrl}?${queryParams}`;
        return this.cache.promise(url, () => axios.get(url).then(({ data: res }) => res));
    }
    async saveItem(data) {
        const { queryId, slugId, folderPath, root, name } = data;
        if (queryId && slugId) {
            throw new Error("Cannot provide both queryId and slugId");
        }
        if (!queryId && !slugId) {
            throw new Error("Must provide either queryId or slugId");
        }
        let lookQueryId = queryId;
        if (slugId) {
            const { id } = await this.sdk.looker.query.createFromSlug(slugId);
            lookQueryId = id;
        }
        return await axios
            .post(this.baseUrl, lookDataToRequestBody({
            queryId: lookQueryId,
            name,
            folderPath,
            root
        }), {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(({ data: res }) => res);
    }
    async deleteItem(lookId) {
        return axios
            .post(`${this.sdk.urls.burnsBaseUrl}/looker/soft_delete/${lookId}`)
            .then(({ data: res }) => {
            this.cache.reconcile("delete", res);
            return res;
        });
    }
    filterToQueryParams(filters) {
        const { where } = filters;
        if (!where) {
            throw new Error("No filters provided");
        }
        const root = parseInclusionFilter(filters, "root", ["string"])
            .values()
            .next().value;
        const folderPaths = where.find((f) => f.field === "folderPaths")?.value;
        const fields = where.find((f) => f.field === "fields")?.value ||
            DEFAULT_LOOK_FIELDS;
        if (!root) {
            throw new Error("Missing required filter for root Look directory");
        }
        if (!folderPaths || folderPaths.length === 0) {
            throw new Error("Missing required filter for folderPaths");
        }
        return new URLSearchParams({
            root,
            fields: fields.join(","),
            folder_paths: folderPaths.join(",")
        }).toString();
    }
    async runLook(filters) {
        const lookId = parseInclusionFilter(filters, "id", ["string"]).values().next().value;
        if (!lookId || typeof lookId !== "string") {
            throw new Error("No look ID provided for CSV download");
        }
        const url = `${this.baseUrl}/run_look/${lookId}`;
        return axios
            .get(url, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(({ data }) => data);
    }
    async getLook(lookId) {
        const url = `${this.sdk.urls.burnsBaseUrl}/looker/look/${lookId}`;
        return this.cache.promise(url, () => axios.get(url).then(({ data }) => data));
    }
    async updateLook(lookData) {
        const { lookId, qid, name, root, folderPath } = lookData;
        const qidIsValid = qid && qid.trim() !== "";
        const nameIsValid = name && name.trim() !== "";
        const folderPathIsValid = folderPath && folderPath.trim() !== "";
        if (!qidIsValid && !nameIsValid && !root && !folderPathIsValid) {
            throw new Error("At least one of qid, name or root and folderPath must be provided to update a Look");
        }
        if ((!root && folderPathIsValid) || (root && !folderPathIsValid)) {
            throw new Error("Both root and folder_path are required to move a look");
        }
        const params = new URLSearchParams();
        if (qidIsValid) {
            params.append("qid", qid.trim());
        }
        if (nameIsValid) {
            params.append("name", name.trim());
        }
        if (root) {
            params.append("root", root);
        }
        if (folderPathIsValid) {
            params.append("folder_path", folderPath.trim());
        }
        return axios
            .patch(`${this.baseUrl}/${lookId}?${params.toString()}`)
            .then(({ data }) => {
            // We need to clear the cache after update because it will have stale look data
            this.cache.clear();
            return data;
        });
    }
}
export default LookHandler;
//# sourceMappingURL=index.js.map